import React from "react";
import PropTypes from "prop-types";
import "./TinyLoading.css";

/**
 * Tiny 後臺共用的 loading 畫面（v2.40.0 建立，v2.42.0 全站統一）
 *
 * # 為什麼要有這個
 *
 * 2026-09-18 Brad 反映「JP Banker 試算跑很久，畫面只有『載入中…』三個字，會以為卡住了」。
 * 查下去是主題 SCSS 沒 import Bootstrap 的 `spinners` partial，`.spinner-border` 沒有寬高 ——
 * **全站 27 個檔、55 處 <Spinner> 從來沒有畫出來過**。那一行 import 已補上。
 * 同一天他又指出：各頁的 pending 有的彈 modal、有的在內容區轉、有的整區空白到資料回來，
 * 於是 v2.42.0 把「什麼情境用什麼」定成規則，全站換成這一套。
 *
 * # 規則（🔑 選元件先看情境，不看喜好）
 *
 *   ① 首屏 / 該區還沒有資料          <TinyLoading>          內容區，不彈 modal、不擋整頁
 *   ② 已有資料的重抓（切週 / tab / 篩選）<TinyLoadingOverlay>   蓋在該區上，舊資料留著不閃掉
 *   ③ 按鈕動作（儲存 / 匯出 / 查詢 / 上傳）<TinyBusy light>       放按鈕裡 + disabled
 *   ④ 背景 job（pipeline / 掃描）輪詢     <TinyBusy text=…>      狀態列裡，不擋任何操作
 *
 *   🚫 不再用：全屏 modal（SimpleLoadingModal / CustomSpinner fullscreen）擋資料讀取、
 *      reactstrap <Spinner>、font-awesome `fa-spinner`、純文字「載入中…」。
 *      那三個舊元件檔案還留著（不刪 code），但不要再從新頁面 import。
 *
 *   三個原則：一定有東西在動（轉圈 + 三個點）；已經有資料就不要把整頁換成空白；
 *   文案只講「在做什麼」，不解釋為什麼慢。
 *
 * 不吃任何 Bootstrap class，純 CSS —— 主題再漏 import 也不會隱形。
 * 色票走 CSS 變數（--tl-accent / --tl-text / --tl-muted），預設值對齊 adminTheme。
 */

const Dots = () => (
  <span className="tiny-loading__dots" aria-hidden="true">
    <span>.</span>
    <span>.</span>
    <span>.</span>
  </span>
);

export default function TinyLoading({ text, hint, variant, style }) {
  return (
    <div
      className={`tiny-loading tiny-loading--${variant}`}
      style={style}
      role="status"
      aria-live="polite"
    >
      <div className="tiny-loading__ring" />
      <div className="tiny-loading__text">
        {text}
        <Dots />
      </div>
      {hint && <div className="tiny-loading__hint">{hint}</div>}
    </div>
  );
}

TinyLoading.propTypes = {
  /** 在做什麼，不要加「…」—— 三個點由元件動態畫 */
  text: PropTypes.string,
  /** 第二行小字（例如「首次開啟要整理全部週期，約數秒」） */
  hint: PropTypes.string,
  /** page：撐滿內容區（首屏）；block：塞在卡片 / 表格位置；compact：很矮的一行（表格列、小卡片） */
  variant: PropTypes.oneOf(["page", "block", "compact"]),
  style: PropTypes.object,
};
TinyLoading.defaultProps = {
  text: "載入中",
  hint: null,
  variant: "page",
  style: undefined,
};

/**
 * 行內小圈圈。
 *   <TinyBusy text="更新中" />                  放在標題 / 狀態列旁
 *   <Button disabled={saving}><TinyBusy light /> 儲存</Button>   放在按鈕裡（白色圈、無字）
 */
export function TinyBusy({ text, light, style }) {
  return (
    <span
      className={`tiny-busy${light ? " tiny-busy--light" : ""}`}
      style={style}
      role="status"
      aria-live="polite"
    >
      <span className="tiny-busy__ring" />
      {text && <span>{text}</span>}
    </span>
  );
}
TinyBusy.propTypes = { text: PropTypes.string, light: PropTypes.bool, style: PropTypes.object };
TinyBusy.defaultProps = { text: null, light: false, style: undefined };

/**
 * 把既有內容包起來，`active` 時蓋一層半透明遮罩 + 圈圈：
 *
 *   <TinyLoadingOverlay active={loading && !!rows.length} text="重新整理">
 *     <Table>…</Table>
 *   </TinyLoadingOverlay>
 *
 * `fixed` 時遮罩蓋整個視窗（給「整頁都是同一份資料、切週全部一起換」的頁面用），
 * 但**不加深色背景、不擋捲動**，只是把圈圈釘在畫面中間。
 */
export function TinyLoadingOverlay({ active, text, children, style, fixed }) {
  return (
    <div className="tiny-loading-overlay" style={style}>
      {children}
      {active && (
        <div className={`tiny-loading-overlay__mask${fixed ? " tiny-loading-overlay__mask--fixed" : ""}`}>
          <TinyLoading text={text} variant="block" />
        </div>
      )}
    </div>
  );
}
TinyLoadingOverlay.propTypes = {
  active: PropTypes.bool,
  text: PropTypes.string,
  children: PropTypes.node,
  style: PropTypes.object,
  fixed: PropTypes.bool,
};
TinyLoadingOverlay.defaultProps = { active: false, text: "更新中", children: null, style: undefined, fixed: false };
